<template>
    <div style="z-index: 200; padding: 14px; min-height: 500px;">
        <block v-if="loading && step != 3"></block>
        <v-stepper v-model="step" class="white pa-4">
            <v-stepper-header v-if="!providerImport" style="flex-wrap: nowrap;">
                <v-stepper-step :complete="step > 1" step="1">
                    Carga de datos
                </v-stepper-step>
                <v-stepper-step :complete="step > 2" step="2">
                    Previsualización de los datos
                </v-stepper-step>
                <v-stepper-step step="3">
                    Procesamiento de los datos
                </v-stepper-step>
            </v-stepper-header>
            <v-stepper-header v-if="providerImport" style="flex-wrap: nowrap; justify-content: space-around;">
                <v-stepper-step :complete="step > 1" step="1">
                    Carga de datos
                </v-stepper-step>
                <v-stepper-step :complete="step > 2" step="2">
                    Datos de los proveedores importados
                </v-stepper-step>
            </v-stepper-header>
            <div style="margin: 1rem 0; display: flex; flex-direction: row; justify-content: space-between;">
                <div style="display: flex; gap: 1rem;">
                    <v-btn 
                        class="btn-save" 
                        @click="downloadTemplate" 
                        :disabled="loading || !hasWarehousesConfigured || generatingTemplate"
                    >
                        <v-icon class="mr-2">mdi-download</v-icon>
                        {{ generatingTemplate ? 'Generando plantilla...' : 'Descargar plantilla para importar existencias' }}
                    </v-btn>
                    <v-btn 
                        class="btn-save" 
                        @click="loadWarehouses" 
                        :disabled="loading"
                    >
                        <v-icon class="mr-2">mdi-refresh</v-icon>Recargar bodegas ({{ availableWarehouses.length }})
                    </v-btn>
                    <v-btn 
                        class="btn-save" 
                        @click="loadProducts" 
                        :disabled="loading"
                    >
                        <v-icon class="mr-2">mdi-package-variant</v-icon>Recargar productos ({{ availableProducts.length }})
                    </v-btn>
                </div>
                <v-btn class="btnclean" @click="resetAll" :disabled="loading">
                    <v-icon class="mr-2">mdi-broom</v-icon>Reiniciar
                </v-btn>
            </div>
            
            <!-- Barra de progreso para la generación de plantilla -->
            <v-row v-if="generatingTemplate">
                <v-col cols="12">
                    <v-card class="pa-4 mb-4" color="blue lighten-5">
                        <v-card-text>
                            <div class="d-flex align-center mb-2">
                                <v-icon color="primary" class="mr-2">mdi-file-excel</v-icon>
                                <span class="font-weight-medium">Generando plantilla de Excel...</span>
                            </div>
                            <v-progress-linear
                                :value="templateProgress"
                                color="primary"
                                height="8"
                                rounded
                                striped
                            ></v-progress-linear>
                            <div class="mt-2 text-caption text-center">
                                {{ templateProgressText }}
                            </div>
                        </v-card-text>
                    </v-card>
                </v-col>
            </v-row>
            <v-alert v-if="availableWarehouses.length === 0 && !loading" type="error" class="mb-4">
                <v-icon left>mdi-alert-circle</v-icon>
                <strong>No hay bodegas configuradas:</strong> 
                Debe configurar al menos una bodega en el sistema antes de poder importar existencias.
            </v-alert>
              <v-stepper-items>
                <!-- Step 1: Upload Excel -->
                <v-stepper-content step="1">
                    <v-row>
                        <v-col cols="12">
                            <v-card class="card-step">
                                <v-card-title class="font-weight-bold text-nowrap">Importar datos desde archivo Excel</v-card-title>
                                <v-card-actions>
                                    <v-row>
                                        <v-col cols="12">
                                            <v-file-input
                                                v-model="file"
                                                label="Selecciona el archivo Excel para importar existencias"
                                                accept=".xlsx,.xls"
                                                prepend-icon="mdi-file-excel"
                                                show-size
                                                @change="handleFileUpload"
                                                :disabled="loading"
                                                outlined
                                                dense
                                            ></v-file-input>
                                        </v-col>
                                    </v-row>
                                </v-card-actions>
                                
                                <v-card-actions v-if="worksheets.length > 0">
                                    <v-row>
                                        <v-col cols="12">
                                            <v-select
                                                v-model="selectedWorksheet"
                                                :items="worksheets"
                                                label="Selecciona la hoja de trabajo"
                                                @change="loadWorksheetData"
                                                :disabled="loading"
                                                outlined
                                                dense
                                            ></v-select>
                                        </v-col>
                                    </v-row>
                                </v-card-actions>
                                
                                <v-card-text v-if="rawData.length > 0">
                                    <h3>Vista previa de los datos:</h3>
                                    <v-data-table
                                        :headers="previewHeaders"
                                        :items="rawData"
                                        class="elevation-1 data-table"
                                        dense
                                        :items-per-page="10"
                                        :footer-props="{
                                            'items-per-page-options': [5, 10, 25, 50],
                                            'items-per-page-text': 'Filas por página:',
                                            'page-text': '{0}-{1} de {2}'
                                        }"
                                    ></v-data-table>
                                </v-card-text>
                                
                                <v-card-actions style="padding: 1rem; justify-content: flex-end;">
                                    <v-btn color="primary" @click="goToPreview" :disabled="loading || rawData.length === 0">
                                        Vista Previa
                                        <v-icon right>mdi-eye</v-icon>
                                    </v-btn>
                                </v-card-actions>
                            </v-card>
                        </v-col>
                    </v-row>
                </v-stepper-content>
                
                <!-- Step 2: Preview -->
                <v-stepper-content step="2">
                    <v-row>
                        <v-col cols="12">
                            <v-card class="card-step">
                                <v-card-title class="font-weight-bold text-nowrap">Vista previa de los datos</v-card-title>
                                <v-card-text>
                                    <p>Revisa los datos y el estado de existencia de cada producto antes de procesar la importación:</p>
                                </v-card-text>

                                <!-- Alerta para productos fallidos -->
                                <v-card-text v-if="hasFailedProducts && !checkingProducts">
                                    <v-alert
                                        type="error"
                                        border="left"
                                        colored-border
                                        elevation="2"
                                        class="mb-4"
                                    >
                                        <div class="d-flex align-center">
                                            <v-icon color="error" class="mr-2">mdi-alert-circle</v-icon>
                                            <div>
                                                <div class="font-weight-bold">Se encontraron {{ failedProducts.length }} productos con errores</div>
                                                <div class="text-caption">
                                                    Estos productos no podrán ser procesados hasta que se corrijan los errores.
                                                </div>
                                            </div>
                                        </div>
                                        
                                        <div class="mt-3 d-flex gap-2">
                                            <v-btn
                                                small
                                                color="error"
                                                outlined
                                                @click="toggleFailedProductsFilter"
                                            >
                                                <v-icon left small>{{ showOnlyFailedProducts ? 'mdi-filter-off' : 'mdi-filter' }}</v-icon>
                                                {{ showOnlyFailedProducts ? 'Mostrar todos' : 'Solo productos fallidos' }}
                                            </v-btn>
                                            
                                            <v-btn
                                                small
                                                color="error"
                                                outlined
                                                @click="exportFailedProducts"
                                            >
                                                <v-icon left small>mdi-file-excel</v-icon>
                                                Exportar errores
                                            </v-btn>
                                        </div>
                                    </v-alert>
                                </v-card-text>

                                <v-card-text v-if="previewData.length > 0">
                                    <v-data-table
                                        :headers="previewDataHeaders"
                                        :items="displayedProducts"
                                        class="elevation-1 data-table"
                                        dense
                                        :items-per-page="15"
                                        :footer-props="{
                                            'items-per-page-options': [10, 15, 25, 50],
                                            'items-per-page-text': 'Registros por página:',
                                            'page-text': '{0}-{1} de {2}'
                                        }"
                                    >
                                        <template v-slot:item.status="{ item }">
                                            <v-chip
                                                :color="item.status === 'Existe' ? 'success' : 'error'"
                                                :text-color="'white'"
                                                small
                                            >
                                                {{ item.status }}
                                            </v-chip>
                                        </template>
                                        
                                        <template v-slot:item.dateValid="{ item }">
                                            <v-icon
                                                v-if="item.expiryDate"
                                                :color="item.dateValid ? 'success' : 'error'"
                                                small
                                                :title="item.dateValid ? 'Fecha válida' : item.dateValidationMessage"
                                            >
                                                {{ item.dateValid ? 'mdi-check-circle' : 'mdi-alert-circle' }}
                                            </v-icon>
                                            <span v-else class="text--disabled">N/A</span>
                                        </template>
                                        
                                        <template v-slot:item.expiryDate="{ item }">
                                            <div>
                                                <div>{{ item.expiryDate || 'Sin fecha' }}</div>
                                                <div v-if="item.originalDateValue && item.originalDateValue !== item.expiryDate" 
                                                     class="text-caption text--secondary">
                                                    Original: {{ item.originalDateValue }}
                                                </div>
                                            </div>
                                        </template>
                                        
                                        <!-- Templates dinámicos para columnas de bodegas -->
                                        <template v-for="warehouse in availableWarehouses" :slot="`item.warehouse_${warehouse.id}`" slot-scope="{ item }">
                                            <div class="text-center" :key="warehouse.id">
                                                {{ item[`warehouse_${warehouse.id}`] }}
                                            </div>
                                        </template>
                                    </v-data-table>
                                </v-card-text>


                                <v-card-text v-if="checkingProducts" class="text-center">
                                    <v-progress-circular
                                        indeterminate
                                        color="primary"
                                        size="32"
                                        class="mr-2"
                                    ></v-progress-circular>
                                    <span>Verificando existencia de productos...</span>
                                </v-card-text>

                                <v-card-actions style="padding: 1rem; justify-content: space-between;">
                                    <v-btn @click="step = 1" :disabled="loading">
                                        <v-icon left>mdi-arrow-left</v-icon>
                                        Anterior
                                    </v-btn>
                                    <v-btn 
                                        color="success" 
                                        @click="processStockImport" 
                                        :disabled="loading || checkingProducts"
                                    >
                                        Procesar Importación
                                        <v-icon right>mdi-upload</v-icon>
                                    </v-btn>
                                </v-card-actions>
                            </v-card>
                        </v-col>
                    </v-row>
                </v-stepper-content>
                
                <!-- Step 3: Processing -->
                <v-stepper-content step="3">
                    <v-row>
                        <v-col cols="12">
                            <!-- Procesando con progreso por lotes -->
                            <v-card v-if="loading && batchProcessing.isProcessing" class="card-step">
                                <v-card-title class="font-weight-bold text-nowrap">
                                    Procesando importación por lotes...
                                </v-card-title>
                                <v-card-text>
                                    <!-- Progreso general -->
                                    <div class="mb-4">
                                        <div class="d-flex justify-space-between align-center mb-2">
                                            <span class="font-weight-medium">Progreso general</span>
                                            <span class="text-caption">
                                                {{ batchProcessing.processedRecords }}/{{ batchProcessing.totalRecords }} registros
                                            </span>
                                        </div>
                                        <v-progress-linear
                                            :value="batchProcessing.overallProgress"
                                            color="primary"
                                            height="12"
                                            rounded
                                        ></v-progress-linear>
                                        <div class="text-center mt-1 text-caption">
                                            {{ Math.round(batchProcessing.overallProgress) }}%
                                        </div>
                                    </div>

                                    <!-- Progreso del lote actual -->
                                    <div class="mb-4">
                                        <div class="d-flex justify-space-between align-center mb-2">
                                            <span class="font-weight-medium">
                                                Lote {{ batchProcessing.currentBatch }}/{{ batchProcessing.totalBatches }}
                                            </span>
                                            <span class="text-caption">
                                                Procesando...
                                            </span>
                                        </div>
                                        <v-progress-linear
                                            :value="batchProcessing.currentBatchProgress"
                                            color="secondary"
                                            height="8"
                                            rounded
                                            striped
                                        ></v-progress-linear>
                                    </div>

                                    <!-- Estadísticas en tiempo real -->
                                    <v-row class="text-center">
                                        <v-col cols="4">
                                            <div class="text-h6 success--text">{{ batchProcessing.successfulBatches }}</div>
                                            <div class="text-caption">Lotes exitosos</div>
                                        </v-col>
                                        <v-col cols="4">
                                            <div class="text-h6 error--text">{{ batchProcessing.failedBatches }}</div>
                                            <div class="text-caption">Lotes fallidos</div>
                                        </v-col>
                                        <v-col cols="4">
                                            <div class="text-h6 primary--text">{{ batchProcessing.batchSize }}</div>
                                            <div class="text-caption">Tamaño del lote</div>
                                        </v-col>
                                    </v-row>
                                </v-card-text>
                            </v-card>

                            <!-- Procesando (modo simple) -->
                            <v-card v-else-if="loading" class="card-step">
                                <v-card-title class="font-weight-bold text-nowrap">
                                    Procesando importación de existencias...
                                </v-card-title>
                                <v-card-text class="text-center">
                                    <v-progress-circular
                                        indeterminate
                                        color="primary"
                                        size="64"
                                        class="ma-4"
                                    ></v-progress-circular>
                                </v-card-text>
                            </v-card>
                            
                            <!-- Resultados finales -->
                            <v-card v-else class="card-step">
                                <v-card-title class="font-weight-bold text-nowrap">Estado del procesamiento de importación</v-card-title>
                                
                                <v-card-text v-if="importResults">
                                    <!-- Resultado exitoso -->
                                    <v-alert
                                        v-if="importResults.success"
                                        type="success"
                                        class="ma-4"
                                    >
                                        <div class="d-flex align-center mb-2">
                                            <v-icon color="success" class="mr-2">mdi-check-circle</v-icon>
                                            <span class="font-weight-bold">¡Importación completada!</span>
                                        </div>
                                        
                                        <v-divider class="my-3"></v-divider>
                                        
                                        <div class="mb-2">
                                            <strong>Resumen de la importación:</strong>
                                        </div>
                                        
                                        <div class="ml-4">
                                            <div><strong>Registros procesados:</strong> {{ importResults.totalProcessed || 0 }}</div>
                                            <div><strong>Productos únicos:</strong> {{ importResults.summary?.totalUniqueProductsProcessed || 0 }}</div>
                                            <div><strong>Ajustes de inventario creados:</strong> {{ importResults.totalAdjustments || 0 }}</div>
                                            <div><strong>Bodegas actualizadas:</strong> {{ importResults.totalWarehouses || 0 }}</div>
                                            <div v-if="importResults.invalidCount > 0" class="error--text">
                                                <strong>Registros con errores:</strong> {{ importResults.invalidCount }}
                                            </div>
                                        </div>

                                        <!-- Información de lotes si está disponible -->
                                        <div v-if="importResults.details?.batchProcessing" class="mt-3">
                                            <v-divider class="my-2"></v-divider>
                                            <div class="mb-2">
                                                <strong>Procesamiento por lotes:</strong>
                                            </div>
                                            <div class="ml-4">
                                                <div><strong>Total de lotes:</strong> {{ importResults.details.batchProcessing.totalBatches }}</div>
                                                <div class="success--text"><strong>Lotes exitosos:</strong> {{ importResults.details.batchProcessing.successfulBatches }}</div>
                                                <div v-if="importResults.details.batchProcessing.failedBatches > 0" class="error--text">
                                                    <strong>Lotes fallidos:</strong> {{ importResults.details.batchProcessing.failedBatches }}
                                                </div>
                                            </div>
                                        </div>
                                        
                                        <div class="mt-3">
                                            <span class="caption">{{ importResults.message }}</span>
                                        </div>
                                    </v-alert>
                                    
                                    <!-- Resultado con error -->
                                    <v-alert
                                        v-else
                                        type="error"
                                        class="ma-4"
                                    >
                                        <div class="d-flex align-center mb-2">
                                            <v-icon color="error" class="mr-2">mdi-alert-circle</v-icon>
                                            <span class="font-weight-bold">Error en la importación</span>
                                        </div>
                                        
                                        <div class="mt-2">
                                            {{ importResults.message || 'Error desconocido durante la importación' }}
                                        </div>

                                        <!-- Información de lotes si está disponible -->
                                        <div v-if="importResults.details?.batchProcessing" class="mt-3">
                                            <v-divider class="my-2"></v-divider>
                                            <div class="mb-2">
                                                <strong>Estado de los lotes:</strong>
                                            </div>
                                            <div class="ml-4">
                                                <div><strong>Total de lotes:</strong> {{ importResults.details.batchProcessing.totalBatches }}</div>
                                                <div class="success--text"><strong>Lotes exitosos:</strong> {{ importResults.details.batchProcessing.successfulBatches }}</div>
                                                <div class="error--text"><strong>Lotes fallidos:</strong> {{ importResults.details.batchProcessing.failedBatches }}</div>
                                            </div>
                                        </div>
                                        
                                        <div v-if="importResults.details" class="mt-2 caption">
                                            <strong>Detalles técnicos:</strong> {{ importResults.details }}
                                        </div>
                                    </v-alert>
                                </v-card-text>

                                <v-card-actions style="padding: 1rem; justify-content: center;">
                                    <v-btn color="primary" @click="resetAll">
                                        Nueva Importación
                                        <v-icon right>mdi-refresh</v-icon>
                                    </v-btn>
                                </v-card-actions>
                            </v-card>
                        </v-col>
                    </v-row>
                </v-stepper-content>
            </v-stepper-items>
        </v-stepper>
    </div>
</template>

<script>
import * as XLSX from 'xlsx';
import Block from "@/components/Block";
import moment from 'moment';

moment.locale('es');

export default {
    name: "ManageStockImport",
    components: {
        Block
    },
    data() {
        return {
            loading: false,
            step: 1,
            file: null,
            worksheets: [],
            selectedWorksheet: null,
            rawData: [],
            previewHeaders: [],
            availableColumns: [],
            generatingTemplate: false,
            templateProgress: 0,
            templateProgressText: '',
            previewData: [],
            checkingProducts: false,
            showOnlyFailedProducts: false,
            showFailedProductsAlert: false,
            importResults: null,
            providerImport: false,
            availableWarehouses: [],
            warehouses: [],
            availableProducts: [],
            products: [],
            search: {
                business: '',
                branch: '',
                filter: ''
            },
            alert: {
                type: "success",
                show: false,
                header: "",
                body: "",
                options: null
            },
            includeStockSearch: false,
            // Variables para procesamiento por lotes
            batchProcessing: {
                isProcessing: false,
                currentBatch: 0,
                totalBatches: 0,
                batchSize: 100,
                currentBatchProgress: 0,
                overallProgress: 0,
                processedRecords: 0,
                totalRecords: 0,
                successfulBatches: 0,
                failedBatches: 0,
                batchResults: []
            },
        }
    },
    computed: {
        hasWarehousesConfigured() {
            return this.availableWarehouses.length > 0;
        },
        failedProducts() {
            return this.previewData.filter(item => {
                const notExists = item.status === 'No existe' || 
                                 item.status === 'ID inválido' || 
                                 item.status === 'Error al verificar';
                
                let hasInvalidDate = false;
                if (item.expiryDate && item.expiryDate.trim() !== '') {
                    const dateValidation = this.validateExpiryDate(item.expiryDate);
                    hasInvalidDate = !dateValidation.isValid;
                }
                
                return notExists || hasInvalidDate;
            });
        },
        displayedProducts() {
            return this.showOnlyFailedProducts ? this.failedProducts : this.previewData;
        },
        hasFailedProducts() {
            return this.failedProducts.length > 0;
        },
        templateModeInfo() {
            return {
                searchEnabled: this.includeStockSearch,
                description: this.includeStockSearch 
                    ? 'Las plantillas incluirán las existencias actuales de cada producto en cada bodega'
                    : 'Las plantillas solo listarán productos y bodegas sin buscar existencias actuales (modo optimizado)'
            };
        },
        
        previewDataHeaders() {
            const baseHeaders = [
                { text: 'Estado', value: 'status', sortable: false, width: '120px' },
                { text: 'ID Producto', value: 'productId', sortable: false },
                { text: 'Nombre del Producto', value: 'productName', sortable: false },
                { text: 'Lote', value: 'lote', sortable: false },
                { text: 'Costo', value: 'costo', sortable: false },
                { text: 'Fecha Vencimiento', value: 'expiryDate', sortable: false },
                { text: 'Fecha Válida', value: 'dateValid', sortable: false, width: '100px' }
            ];
            
            const warehouseHeaders = this.availableWarehouses.map(warehouse => {
                const productsWithStock = this.previewData.filter(item => {
                    const stockValue = parseFloat(item[`warehouse_${warehouse.id}`]);
                    return !isNaN(stockValue);
                }).length;
                
                const headerText = productsWithStock > 0 
                    ? `${warehouse.name} (${productsWithStock})`
                    : warehouse.name;
                
                return {
                    text: headerText,
                    value: `warehouse_${warehouse.id}`,
                    sortable: false,
                    width: '120px',
                    class: 'text-center'
                };
            });
            
            return [...baseHeaders, ...warehouseHeaders];
        },

        warehouseTotals() {
            if (!this.previewData.length || !this.availableWarehouses.length) {
                return [];
            }
            
            return this.availableWarehouses.map(warehouse => {
                const total = this.previewData.reduce((sum, item) => {
                    const stockValue = parseFloat(item[`warehouse_${warehouse.id}`]) || 0;
                    return sum + stockValue;
                }, 0);
                
                return {
                    id: warehouse.id,
                    name: warehouse.name,
                    total: total,
                    count: this.previewData.filter(item => {
                        const stockValue = parseFloat(item[`warehouse_${warehouse.id}`]);
                        return !isNaN(stockValue);
                    }).length
                };
            });
        },
    },
    async mounted() {
        this.initializeSearchParams();
        await this.loadWarehouses();
        await this.loadProducts();
    },
    methods: {
        initializeSearchParams() {
            try {
                const user = JSON.parse(localStorage.getItem("user")) || {};
                this.search.business = user.businessid || "";
                this.search.branch = user.branch || "";
                this.search.filter = '';
                console.log('Parámetros de búsqueda inicializados:', this.search);
            } catch (error) {
                console.error('Error al obtener datos del usuario desde localStorage:', error);
                this.search = {
                    business: '',
                    branch: '',
                    filter: ''
                };
            }
        },
        async loadProducts() {
            try {
                this.loading = true;
                if (!this.search.business && !this.search.branch) { 
                    this.initializeSearchParams();
                }
                console.log('Cargando productos con parámetros:', this.search);
                const templateParams = {
                    ID_Business: this.search.business,
                    ID_Branch: this.search.branch
                };
                
                console.log('Parámetros para plantilla:', templateParams);
                
                this.products = await this.$API.products.getProductsForTemplate(templateParams).then(response => response.data);
                
                if (!this.products || this.products.length === 0) {
                    console.warn('No se encontraron productos en el sistema');
                    this.availableProducts = [];
                    return;
                }
                const validProducts = this.products.filter(product => {
                    const hasId = product['ID Producto'];
                    const hasName = product['Nombre del producto'];
                    return hasId && hasName;
                });
                
                this.availableProducts = validProducts.map(product => ({
                    id: product['ID Producto'],
                    name: product['Nombre del producto'] || 'Producto sin nombre',
                    code: product['ID Producto'],
                    barcode: '',
                    description: '',
                    price: 0,
                    measureUnit: 'Unidad',
                    warehouse: product.Warehouse || '',
                    provider: '',
                    stock: 0,
                    isDeleted: 0,
                    lote: product.Lote && product.Lote.trim() !== '' ? product.Lote : null,
                    expiryDate: product['Fecha de vencimiento'] && product['Fecha de vencimiento'].trim() !== '' ? product['Fecha de vencimiento'] : null,
                    warehouseId: product.Warehouse || null,
                    costo: product.costo || 0
                }));
                
                if (this.availableProducts.length > 0) {
                    this.showAlert("success", "Éxito", `Se cargaron ${this.availableProducts.length} productos`);
                } else {
                    this.showAlert("warning", "Advertencia", `No se pudieron procesar productos válidos`);
                }
            } catch (error) {
                console.error('Error al cargar productos:', error);
                this.availableProducts = [];
                this.products = [];
                
                console.warn('No se pudieron cargar productos para la plantilla, se usarán ejemplos genéricos');
            } finally {
                this.loading = false;
            }
        },
        async loadWarehouses() {
            try {
                this.loading = true;
                const { businessid = "", branch = "" } = JSON.parse(localStorage.getItem("user"));
                const filtersWh = { business: businessid, branch, notShowReserve: 1};
                this.warehouses = await this.$API.warehouse.getWarehouses(filtersWh).then(response => response);
                console.log('Bodegas cargadas:', this.warehouses);
                
                if (!this.warehouses || this.warehouses.length === 0) {
                    throw new Error('No se encontraron bodegas configuradas en el sistema');
                }
                const activeBodegas = this.warehouses.filter(warehouse => 
                    warehouse.isDeleted === 0
                );
                
                this.availableWarehouses = activeBodegas.map(warehouse => ({
                    id: warehouse.ID_Warehouse,
                    name: warehouse.Warehouse,
                    code: warehouse.ID_Warehouse,
                    description: warehouse.Warehouse,
                    active: warehouse.Active,
                    isDeleted: warehouse.isDeleted,
                    branch: warehouse.ID_Branch,
                    isGlobalBranch: warehouse.IsGlobalBranch
                }));
                
                if (this.availableWarehouses.length === 0) {
                    throw new Error('No se encontraron bodegas activas configuradas en el sistema');
                }
                
                this.showAlert("success", "Éxito", `Se cargaron ${this.availableWarehouses.length} bodegas desde la base de datos`);
            } catch (error) {
                console.error('Error al cargar bodegas:', error);
                this.availableWarehouses = [];
                this.warehouses = [];
                
                const errorMessage = error.response?.data?.message || error.message || 'Error al cargar las bodegas desde la base de datos';
                this.showAlert("error", "Error", errorMessage);
                
                if (error.message.includes('No se encontraron bodegas')) {
                    this.showAlert("warning", "Advertencia", 'Debe configurar al menos una bodega en el sistema antes de poder importar existencias');
                }
            } finally {
                this.loading = false;
            }
        },
        handleFileUpload(file) {
            if (!file) return;
            
            this.loading = true;
            this.resetData();
            
            const reader = new FileReader();
            reader.onload = (e) => {
                try {
                    const data = new Uint8Array(e.target.result);
                    const workbook = XLSX.read(data, { type: 'array' });
                    this.worksheets = workbook.SheetNames.map(name => ({ text: name, value: name }));
                    if (this.worksheets.length === 1) {
                        this.selectedWorksheet = this.worksheets[0].value;
                        this.loadWorksheetData();
                    }
                } catch (error) {
                    this.showAlert("error", "Error", 'Error al leer el archivo Excel');
                } finally {
                    this.loading = false;
                }
            };
            reader.readAsArrayBuffer(file);
        },

        loadWorksheetData() {
            if (!this.file || !this.selectedWorksheet) return;
            
            this.loading = true;
            const reader = new FileReader();
            reader.onload = (e) => {
                try {
                    const data = new Uint8Array(e.target.result);
                    const workbook = XLSX.read(data, { 
                        type: 'array',
                        cellDates: true,
                        cellNF: false,
                        cellText: false
                    });
                    const worksheet = workbook.Sheets[this.selectedWorksheet];
                    
                    const jsonData = XLSX.utils.sheet_to_json(worksheet, { 
                        header: 1,
                        raw: false,
                        dateNF: 'dd/mm/yyyy'
                    });
                    
                    if (jsonData.length > 0) {
                        
                        const headers = jsonData[0];
                        this.previewHeaders = headers.map((header, index) => ({
                            text: header || `Columna ${index + 1}`,
                            value: `col_${index}`,
                            sortable: false
                        }));
                        
                        const dataRows = jsonData.slice(1);
                        const filteredRows = dataRows.filter(row => {
                            const warehouseColumns = row.slice(5);
                            
                            return warehouseColumns.some(value => {
                                const numericValue = parseFloat(value);
                                return !isNaN(numericValue);
                            });
                        });
                        
                        this.rawData = filteredRows.map(row => {
                            const obj = {};
                            headers.forEach((header, index) => {
                                obj[`col_${index}`] = row[index] || '';
                            });
                            return obj;
                        });
                        
                        this.availableColumns = headers.map((header, index) => ({
                            text: header || `Columna ${index + 1}`,
                            value: `col_${index}`
                        }));
                        
                        const originalCount = dataRows.length;
                        const filteredCount = filteredRows.length;
                        if (originalCount > filteredCount) {
                            this.showAlert("info", "Información", 
                                `Se filtraron ${filteredCount} productos de ${originalCount} que tienen valores numéricos válidos en al menos una bodega`
                            );
                        }
                    }
                } catch (error) {
                    this.showAlert("error", "Error", 'Error al procesar los datos del Excel');
                } finally {
                    this.loading = false;
                }
            };
            reader.readAsArrayBuffer(this.file);
        },
        async processStockImport() {
            this.loading = true;
            this.step = 3;
            
            try {
                const finalDataToSend = this.prepareFinalImportData();
                console.log('Datos finales preparados para enviar (estructura plana):', finalDataToSend);
                
                // Filtrar solo registros válidos (cada registro es una combinación producto-bodega)
                const validProducts = finalDataToSend.filter(productWarehouse => {
                    const existsInSystem = productWarehouse.isValid && productWarehouse.validationStatus === 'Existe';
                    const hasValidDate = !productWarehouse.expiryDate || productWarehouse.hasValidDate;
                    
                    return existsInSystem && hasValidDate;
                });
                
                const invalidProducts = finalDataToSend.length - validProducts.length;
                
                if (validProducts.length === 0) {
                    throw new Error('No hay registros válidos para importar existencias. Todos los registros han sido descartados por errores de validación.');
                }
                
                if (invalidProducts > 0) {
                    console.warn(`Se descartaron ${invalidProducts} registros por errores de validación`);
                    this.showAlert("warning", "Registros Descartados", 
                        `Se descartaron ${invalidProducts} registros por errores de existencia o fecha inválida. Solo se procesarán ${validProducts.length} registros válidos.`
                    );
                }

                // Inicializar procesamiento por lotes
                await this.processBatchedImport(validProducts);
                
            } catch (error) {
                console.error('Error en processStockImport:', error);
                this.importResults = {
                    success: false,
                    message: error.message || 'Error desconocido',
                    error: true
                };
                this.showAlert("error", "Error en Importación", error.message || 'Error al importar existencias');
            } finally {
                this.loading = false;
                this.resetBatchProcessing();
            }
        },

        async processBatchedImport(validProducts) {
            // Configurar lotes
            this.batchProcessing.isProcessing = true;
            this.batchProcessing.totalRecords = validProducts.length;
            this.batchProcessing.totalBatches = Math.ceil(validProducts.length / this.batchProcessing.batchSize);
            this.batchProcessing.currentBatch = 0;
            this.batchProcessing.processedRecords = 0;
            this.batchProcessing.successfulBatches = 0;
            this.batchProcessing.failedBatches = 0;
            this.batchProcessing.batchResults = [];

            console.log(`Iniciando procesamiento por lotes: ${this.batchProcessing.totalBatches} lotes de ${this.batchProcessing.batchSize} registros`);

            let allResults = {
                totalRecordsReceived: 0,
                totalRecordsProcessed: 0,
                totalUniqueProductsProcessed: new Set(),
                totalAdjustmentsCreated: 0,
                totalWarehousesUpdated: new Set(),
                invalidRecordsCount: 0
            };

            // Procesar cada lote secuencialmente
            for (let i = 0; i < this.batchProcessing.totalBatches; i++) {
                this.batchProcessing.currentBatch = i + 1;
                this.batchProcessing.currentBatchProgress = 0;

                const startIndex = i * this.batchProcessing.batchSize;
                const endIndex = Math.min(startIndex + this.batchProcessing.batchSize, validProducts.length);
                const batch = validProducts.slice(startIndex, endIndex);

                console.log(`Procesando lote ${this.batchProcessing.currentBatch}/${this.batchProcessing.totalBatches} (${batch.length} registros)`);

                try {
                    // Actualizar progreso del lote actual
                    this.batchProcessing.currentBatchProgress = 25;
                    await this.$nextTick();

                    const batchData = {
                        products: batch,
                        batchInfo: {
                            batchNumber: this.batchProcessing.currentBatch,
                            totalBatches: this.batchProcessing.totalBatches,
                            batchSize: batch.length,
                            recordsInBatch: batch.length
                        }
                    };

                    this.batchProcessing.currentBatchProgress = 50;
                    await this.$nextTick();

                    // Enviar lote al backend
                    const response = await this.$API.products.importarExistenciaProducto(batchData);

                    this.batchProcessing.currentBatchProgress = 75;
                    await this.$nextTick();

                    console.log(`Respuesta del lote ${this.batchProcessing.currentBatch}:`, response);

                    if (response.success) {
                        this.batchProcessing.successfulBatches++;
                        
                        // Acumular resultados
                        if (response.data?.summary) {
                            const summary = response.data.summary;
                            allResults.totalRecordsReceived += summary.totalRecordsReceived || 0;
                            allResults.totalRecordsProcessed += summary.totalRecordsProcessed || 0;
                            allResults.totalAdjustmentsCreated += summary.totalAdjustmentsCreated || 0;
                            allResults.invalidRecordsCount += summary.invalidRecordsCount || 0;
                            
                            // Acumular productos únicos procesados
                            if (response.data.details?.processedProducts) {
                                response.data.details.processedProducts.forEach(productId => {
                                    allResults.totalUniqueProductsProcessed.add(productId);
                                });
                            }
                            
                            // Acumular bodegas únicas procesadas
                            if (response.data.details?.processedWarehouses) {
                                response.data.details.processedWarehouses.forEach(warehouseId => {
                                    allResults.totalWarehousesUpdated.add(warehouseId);
                                });
                            }
                        }

                        this.batchProcessing.batchResults.push({
                            batchNumber: this.batchProcessing.currentBatch,
                            success: true,
                            recordsProcessed: batch.length,
                            message: response.message
                        });
                    } else {
                        throw new Error(response.message || 'Error en el procesamiento del lote');
                    }

                    this.batchProcessing.currentBatchProgress = 100;
                    this.batchProcessing.processedRecords += batch.length;
                    this.batchProcessing.overallProgress = (this.batchProcessing.processedRecords / this.batchProcessing.totalRecords) * 100;

                    await this.$nextTick();
                    

                } catch (error) {
                    console.error(`Error en lote ${this.batchProcessing.currentBatch}:`, error);
                    this.batchProcessing.failedBatches++;
                    
                    this.batchProcessing.batchResults.push({
                        batchNumber: this.batchProcessing.currentBatch,
                        success: false,
                        recordsProcessed: 0,
                        message: error.message || 'Error desconocido en el lote',
                        error: true,
                        errorDetails: {
                            type: error.name || 'UnknownError',
                            code: error.code || 'UNKNOWN_ERROR'
                        }
                    });

                    // Decidir si continuar o detener el procesamiento
                    const shouldContinue = await this.handleBatchError(error, this.batchProcessing.currentBatch);
                    if (!shouldContinue) {
                        console.warn(`Deteniendo procesamiento en lote ${this.batchProcessing.currentBatch} debido a error crítico`);
                        break;
                    }
                }
            }

            // Preparar resultados finales
            const finalResults = {
                totalRecordsReceived: allResults.totalRecordsReceived,
                totalRecordsProcessed: allResults.totalRecordsProcessed,
                totalUniqueProductsProcessed: allResults.totalUniqueProductsProcessed.size,
                totalAdjustmentsCreated: allResults.totalAdjustmentsCreated,
                totalWarehousesUpdated: allResults.totalWarehousesUpdated.size,
                invalidRecordsCount: allResults.invalidRecordsCount
            };

            this.importResults = {
                success: this.batchProcessing.successfulBatches > 0,
                message: this.generateFinalMessage(),
                summary: finalResults,
                details: {
                    batchProcessing: {
                        totalBatches: this.batchProcessing.totalBatches,
                        successfulBatches: this.batchProcessing.successfulBatches,
                        failedBatches: this.batchProcessing.failedBatches,
                        batchResults: this.batchProcessing.batchResults
                    },
                    processedProducts: Array.from(allResults.totalUniqueProductsProcessed),
                    processedWarehouses: Array.from(allResults.totalWarehousesUpdated)
                },
                totalProcessed: finalResults.totalRecordsProcessed,
                totalAdjustments: finalResults.totalAdjustmentsCreated,
                totalWarehouses: finalResults.totalWarehousesUpdated,
                invalidCount: finalResults.invalidRecordsCount
            };

            if (this.batchProcessing.successfulBatches > 0) {
                this.showAlert("success", "Importación Completada", this.generateFinalMessage());
            } else {
                this.showAlert("error", "Importación Fallida", 'Todos los lotes fallaron en el procesamiento');
            }
        },

        async handleBatchError(error, batchNumber) {
            // Por defecto, continuar con el siguiente lote a menos que sea un error crítico
            if (error.message && error.message.includes('servidor no disponible')) {
                console.error(`Error crítico del servidor en lote ${batchNumber}, deteniendo procesamiento`);
                return false;
            }
            
            if (error.message && error.message.includes('autenticación')) {
                console.error(`Error de autenticación en lote ${batchNumber}, deteniendo procesamiento`);
                return false;
            }
            
            console.warn(`Lote ${batchNumber} falló con error recuperable, continuando con el siguiente lote:`, error.message);
            return true;
        },

        generateFinalMessage() {
            const total = this.batchProcessing.totalBatches;
            const successful = this.batchProcessing.successfulBatches;
            const failed = this.batchProcessing.failedBatches;
            const totalRecords = this.batchProcessing.totalRecords;
            const processedRecords = this.batchProcessing.processedRecords;
            
            if (failed === 0) {
                return `Importación completada exitosamente. Se procesaron ${successful}/${total} lotes correctamente (${processedRecords} registros en total).`;
            } else if (successful === 0) {
                return `La importación falló completamente. ${failed}/${total} lotes fallaron. No se procesaron registros.`;
            } else {
                return `Importación parcialmente exitosa. ${successful}/${total} lotes exitosos, ${failed} lotes fallaron. Se procesaron ${processedRecords} de ${totalRecords} registros.`;
            }
        },

        resetBatchProcessing() {
            this.batchProcessing = {
                isProcessing: false,
                currentBatch: 0,
                totalBatches: 0,
                batchSize: 100,
                currentBatchProgress: 0,
                overallProgress: 0,
                processedRecords: 0,
                totalRecords: 0,
                successfulBatches: 0,
                failedBatches: 0,
                batchResults: []
            };
        },

        prepareFinalImportData() {
            const flatProducts = [];
            
            this.previewData.forEach((previewItem, index) => {
                const rawRow = this.rawData[index];
                
                const existsInSystem = previewItem.status === 'Existe';
                const dateValidation = this.validateExpiryDate(previewItem.expiryDate);
                const isCompletelyValid = existsInSystem && dateValidation.isValid;
                
                // Datos base del producto
                const baseProductData = {
                    productId: previewItem.productId || '',
                    productName: previewItem.productName || '',
                    lote: previewItem.lote || '',
                    costo: previewItem.costo || '',
                    expiryDate: dateValidation.isValid ? (dateValidation.formattedDate || previewItem.expiryDate) : previewItem.expiryDate,
                    
                    isValid: isCompletelyValid,
                    validationStatus: previewItem.status,
                    existsInSystem: existsInSystem,
                    
                    hasValidDate: dateValidation.isValid,
                    dateValidationMessage: dateValidation.message,
                    
                    validationErrors: [
                        ...(existsInSystem ? [] : ['Producto no existe en el sistema']),
                        ...(dateValidation.isValid ? [] : [`Fecha inválida: ${dateValidation.message}`])
                    ]
                };
                
                // Crear un registro separado por cada bodega con existencias
                this.previewHeaders.forEach((header, headerIndex) => {
                    if (headerIndex < 5) return; // Saltar las primeras 5 columnas (datos del producto)
                    
                    const warehouseName = header.text;
                    const columnKey = `col_${headerIndex}`;
                    const stockValue = rawRow ? rawRow[columnKey] : '';
                    const numericStock = parseFloat(stockValue);

                    if (!isNaN(numericStock)) {
                        // Buscar el ID real de la bodega basado en el nombre
                        const warehouse = this.availableWarehouses.find(wh => 
                            wh.name === warehouseName || 
                            wh.name === warehouseName.replace(/\s*\(\d+\)$/, '') // Remover contador de productos
                        );
                        
                        const warehouseId = warehouse ? warehouse.id : warehouseName;
                        
                        // Crear un registro plano para esta combinación producto-bodega
                        flatProducts.push({
                            ...baseProductData,
                            warehouseId: warehouseId,
                            warehouseName: warehouseName,
                            stock: numericStock,
                            originalStockValue: stockValue,
                            columnKey: columnKey
                        });
                    }
                });
            });

            return flatProducts;
        },
        async downloadTemplate() {
            if (this.availableWarehouses.length === 0) {
                this.showAlert("error", "Error", 'No es posible generar la plantilla sin bodegas configuradas');
                this.showAlert("warning", "Advertencia", 'Debe configurar bodegas en el sistema antes de descargar la plantilla');
                return;
            }

            this.generatingTemplate = true;
            this.templateProgress = 0;
            this.templateProgressText = 'Preparando datos...';
            
            if (this.availableProducts.length > 5000) {
                this.showAlert("info", "Información", 
                    `Generando plantilla con ${this.availableProducts.length} productos. Esto puede tomar unos minutos...`
                );
            }

            try {
                await this.generateTemplateOptimized();
                const productsIncluded = Math.min(this.availableProducts.length, 15000);
                this.showAlert("success", "Éxito", 
                    `Plantilla generada exitosamente con ${productsIncluded} productos y ${this.availableWarehouses.length} bodegas`
                );
            } catch (error) {
                console.error('Error al generar la plantilla:', error);
                this.showAlert("error", "Error", 'Error al generar la plantilla de Excel');
            } finally {
                this.generatingTemplate = false;
                this.templateProgress = 0;
                this.templateProgressText = '';
            }
        },
        
        async generateTemplateOptimized() {
            this.templateProgress = 10;
            this.templateProgressText = 'Preparando encabezados...';
            await this.$nextTick();
            
            const baseHeaders = ['ID Producto', 'Nombre del Producto', 'lote', 'Costo', 'Formato: 01/01/2025'];
            const warehouseHeaders = this.availableWarehouses.map(wh => wh.name);
            const warehouseCodes = this.availableWarehouses.map(wh => wh.code);
            
            this.templateProgress = 20;
            this.templateProgressText = 'Preparando datos de productos...';
            await this.$nextTick();
            let productData = [];
            const maxProductsInTemplate = 15000;
            
            if (this.availableProducts.length > 0) {
                const productsToUse = this.availableProducts.slice(0, maxProductsInTemplate);
                const totalProducts = productsToUse.length;
                const batchSize = 1000;
                
                this.templateProgressText = `Procesando ${totalProducts} productos en lotes...`;
                
                for (let i = 0; i < totalProducts; i += batchSize) {
                    console.log(`Procesando lote de productos: ${i} a ${i + batchSize}`);
                    const batch = productsToUse.slice(i, i + batchSize);
                    const batchData = await this.processBatchProducts(batch);
                    productData.push(...batchData);
                    
                    const progress = 20 + ((i + batchSize) / totalProducts) * 50;
                    this.templateProgress = Math.min(progress, 70);
                    this.templateProgressText = `Procesados ${Math.min(i + batchSize, totalProducts)} de ${totalProducts} productos...`;
                    await new Promise(resolve => setTimeout(resolve, 2));
                }
                if (this.availableProducts.length > maxProductsInTemplate) {
                    this.showAlert("warning", "Advertencia", 
                        `Se incluyen los primeros ${maxProductsInTemplate} productos de ${this.availableProducts.length} disponibles. Para incluir todos los productos, considere filtrar por categoría o usar múltiples plantillas.`
                    );
                } else {
                    this.showAlert("info", "Información", 
                        `Plantilla generada con todos los ${this.availableProducts.length} productos disponibles`
                    );
                }
            }
            
            this.templateProgress = 75;
            this.templateProgressText = 'Creando estructura de Excel...';
            await this.$nextTick();
            
            const templateData = [
                ['', '', '', '', 'Fecha vencimiento', ...warehouseCodes],
                [...baseHeaders, ...warehouseHeaders],
                ...productData
            ];
            
            this.templateProgress = 85;
            this.templateProgressText = 'Generando archivo Excel...';
            await this.$nextTick();
            const ws = XLSX.utils.aoa_to_sheet(templateData);
            
            this.configureBasicExcelProperties(ws, productData);
            
            this.templateProgress = 95;
            this.templateProgressText = 'Guardando archivo...';
            await this.$nextTick();
            
            const wb = XLSX.utils.book_new();
            XLSX.utils.book_append_sheet(wb, ws, 'Plantilla_Existencias');
            
            const wopts = {
                bookType: 'xlsx',
                bookSST: false,
                type: 'binary',
                cellStyles: false,
                compression: true
            };
            XLSX.writeFile(wb, 'Plantilla_Importacion_Existencias.xlsx', wopts);
            
            this.templateProgress = 100;
            this.templateProgressText = 'Completado';
        },
        
        async processBatchProducts(batch) {
            return batch.map((product) => {
                const realLote = product.lote || '';
                const realCosto = product.costo || '';
                const realExpiryDate = product.expiryDate || '';
                
                const warehouseStocks = this.availableWarehouses.map(warehouse => {
                    if (this.includeStockSearch) {
                        return this.getProductStockByWarehouse(product, warehouse.id);
                    } else {
                        return '';
                    }
                });
                
                return [
                    product.id,
                    product.name,
                    realLote,
                    realCosto,
                    realExpiryDate,
                    ...warehouseStocks
                ];
            });
        },
        
        configureBasicExcelProperties(ws, productData) {
            const baseColumns = [
                { wch: 15 },
                { wch: 25 },
                { wch: 10 },
                { wch: 12 },
                { wch: 20 }
            ];
            const warehouseColumns = this.availableWarehouses.map(() => ({ wch: 12 }));
            ws['!cols'] = [...baseColumns, ...warehouseColumns];

            ws['!freeze'] = { xSplit: 0, ySplit: 2 };

            const dateColumnLetter = 'E';
            const lastDataRow = Math.min(2 + productData.length, 20000);
            ws['!dataValidation'] = {
                [`${dateColumnLetter}3:${dateColumnLetter}${lastDataRow}`]: {
                    type: 'date',
                    formula1: '01/01/2025',
                    formula2: '31/12/2030',
                    showErrorMessage: true,
                    errorTitle: 'Fecha inválida',
                    error: 'Por favor ingrese una fecha válida entre 2025 y 2030'
                }
            };
        },
        generateTemplate() {
            const baseHeaders = ['ID Producto', 'Nombre del Producto', 'lote', 'Costo', 'Formato: 01/01/2025'];
            const warehouseHeaders = this.availableWarehouses.map(wh => wh.name);
            const warehouseCodes = this.availableWarehouses.map(wh => wh.code);
            
            let productData = [];
            
            if (this.availableProducts.length > 0) {
                const productsForTemplate = this.availableProducts;
                
                productData = productsForTemplate.map((product) => {
                    const realLote = product.lote || '';
                    const realCosto = product.costo || '';
                    const realExpiryDate = product.expiryDate || '';
                    
                    const warehouseStocks = this.availableWarehouses.map(warehouse => {
                        if (this.includeStockSearch) {
                            return this.getProductStockByWarehouse(product, warehouse.id);
                        } else {
                            return '';
                        }
                    });
                    
                    return [
                        product.id,
                        product.name,
                        realLote,
                        realCosto,
                        realExpiryDate,
                        ...warehouseStocks
                    ];
                });
            } else {
                productData = [
                    ['PRO2025060001', 'Producto Ejemplo 1', 'L1', '10.50', '01/01/2026', ...this.availableWarehouses.map((_, i) => (i + 1) * 15)],
                    ['PRO2025060002', 'Producto Ejemplo 2', 'L2', '25.75', '15/06/2026', ...this.availableWarehouses.map((_, i) => (i + 1) * 25)],
                    ['PRO2025060003', 'Producto Ejemplo 3', 'L3', '8.00', '31/12/2025', ...this.availableWarehouses.map((_, i) => (i + 1) * 10)]
                ];
            }

            const templateData = [
                ['', '', '', '', 'Fecha vencimiento', ...warehouseCodes],
                [...baseHeaders, ...warehouseHeaders],
                ...productData
            ];
            
            const ws = XLSX.utils.aoa_to_sheet(templateData);
            
            this.configureBasicExcelProperties(ws, productData);

            const wb = XLSX.utils.book_new();
            XLSX.utils.book_append_sheet(wb, ws, 'Plantilla_Existencias');
            
            const wopts = {
                bookType: 'xlsx',
                bookSST: false,
                type: 'binary',
                cellStyles: false,
                compression: true
            };
            XLSX.writeFile(wb, 'Plantilla_Importacion_Existencias.xlsx', wopts);
            this.showAlert("success", "Éxito", `Plantilla generada con ${this.availableWarehouses.length} bodegas`);
        },
        getProductStockByWarehouse(product, warehouseId) {
            if (product.warehouseId === warehouseId) {
                return product.stock || '';
            }
            
            const sameProductInWarehouse = this.products.find(p => 
                p['ID Producto'] === product.id && p.Warehouse === warehouseId
            );
            
            if (sameProductInWarehouse) {
                return sameProductInWarehouse.stock || '';
            }
            
            return '';
        },
        resetAll() {
            this.step = 1;
            this.file = null;
            this.resetData();
            this.importResults = null;
            this.generatingTemplate = false;
            this.templateProgress = 0;
            this.templateProgressText = '';
            this.resetBatchProcessing();
        },
        resetData() {
            this.worksheets = [];
            this.selectedWorksheet = null;
            this.rawData = [];
            this.previewHeaders = [];
            this.availableColumns = [];
            this.previewData = [];
            this.checkingProducts = false;
            this.showOnlyFailedProducts = false;
            this.showFailedProductsAlert = false;
        },
        async goToPreview() {
            this.loading = true;
            this.step = 2;
            
            try {
                this.preparePreviewData();
                
                await this.checkProductsExistence();
                
            } catch (error) {
                console.error('Error al preparar vista previa:', error);
                this.showAlert("error", "Error", 'Error al verificar existencia de productos');
            } finally {
                this.loading = false;
            }
        },
        preparePreviewData() {
            this.previewData = this.rawData.map(row => {
                const formattedDate = this.formatExpiryDate(row.col_4 || '');
                const dateValidation = this.validateExpiryDate(formattedDate);
                
                const productInfo = {
                    productId: row.col_0 || '',
                    productName: row.col_1 || '',
                    lote: row.col_2 || '',
                    costo: row.col_3 || '',
                    expiryDate: formattedDate,
                    dateValid: dateValidation.isValid,
                    dateValidationMessage: dateValidation.message,
                    originalDateValue: row.col_4 || '',
                    status: 'Verificando...'
                };
                
                this.availableWarehouses.forEach((warehouse, index) => {
                    const columnIndex = 5 + index;
                    const stockValue = row[`col_${columnIndex}`] || '';
                    
                    // Solo procesar valores no vacíos y permitir decimales y negativos
                    if (stockValue !== '' && stockValue !== null && stockValue !== undefined) {
                        const numericStock = parseFloat(stockValue);
                        productInfo[`warehouse_${warehouse.id}`] = !isNaN(numericStock) ? numericStock : 0;
                    } else {
                        // Dejar vacío para indicar "no modificar"
                        productInfo[`warehouse_${warehouse.id}`] = '';
                    }
                });
                
                return productInfo;
            });
        },

        formatExpiryDate(dateValue) {
            console.log('Formateando fecha con Moment.js:', dateValue);
            
            if (!dateValue || dateValue.toString().trim() === '') {
                return '';
            }

            try {
                let dateStr = dateValue.toString().trim();
                let momentDate = null;
                
                if (!isNaN(dateStr) && dateStr.length > 4) {
                    const excelDate = parseFloat(dateStr);
                    const jsDate = new Date((excelDate - 25569) * 86400 * 1000);
                    momentDate = moment(jsDate);
                    
                    if (momentDate.isValid()) {
                        return momentDate.format('DD/MM/YYYY');
                    }
                }
                
                const formatsToTry = [
                    'DD/MM/YYYY',
                    'DD-MM-YYYY',
                    'YYYY-MM-DD',
                    'MM/DD/YYYY',
                    'DD/MM/YY',
                    'DD-MM-YY'
                ];
                
                for (const format of formatsToTry) {
                    momentDate = moment(dateStr, format, true);
                    if (momentDate.isValid()) {
                        if (format.includes('YY') && !format.includes('YYYY')) {
                            const year = momentDate.year();
                            if (year < 1950) {
                                momentDate.add(100, 'years');
                            }
                        }
                        
                        return momentDate.format('DD/MM/YYYY');
                    }
                }
                
                momentDate = moment(dateStr);
                if (momentDate.isValid()) {
                    return momentDate.format('DD/MM/YYYY');
                }
                
                console.warn(`Formato de fecha no reconocido con Moment.js: "${dateValue}". Se esperaba formato DD/MM/YYYY`);
                return dateStr;
                
            } catch (error) {
                console.error('Error al formatear fecha con Moment.js:', error, 'Valor original:', dateValue);
                return dateValue.toString();
            }
        },

        formatDateToString(date) {
            if (!date) {
                return '';
            }
            
            const momentDate = moment(date);
            if (!momentDate.isValid()) {
                return '';
            }
            
            return momentDate.format('DD/MM/YYYY');
        },

        validateExpiryDate(dateString) {
            if (!dateString || dateString.trim() === '') {
                return {
                    isValid: true,
                    message: 'Sin fecha de vencimiento',
                    originalValue: dateString
                };
            }
            
            const cleanDate = dateString.trim();
            
            const expectedFormat = 'DD/MM/YYYY';
            
            const momentDate = moment(cleanDate, expectedFormat, true);
            
            if (!momentDate.isValid()) {
                return {
                    isValid: false,
                    message: 'Formato incorrecto. Debe ser DD/MM/YYYY',
                    originalValue: dateString,
                    error: 'INVALID_FORMAT'
                };
            }
            
            const today = moment().startOf('day');
            
            if (momentDate.isBefore(today)) {
                return {
                    isValid: false,
                    message: 'La fecha de vencimiento no puede ser anterior a hoy',
                    originalValue: dateString,
                    error: 'DATE_IN_PAST'
                };
            }
            
            const year = momentDate.year();
            if (year < 2000 || year > 2050) {
                return {
                    isValid: false,
                    message: 'Año fuera de rango (2000-2050)',
                    originalValue: dateString,
                    error: 'INVALID_YEAR'
                };
            }
            
            const tenYearsFromNow = moment().add(10, 'years');
            if (momentDate.isAfter(tenYearsFromNow)) {
                return {
                    isValid: false,
                    message: 'Fecha muy lejana (máximo 10 años desde hoy)',
                    originalValue: dateString,
                    error: 'DATE_TOO_FAR'
                };
            }
            
            return {
                isValid: true,
                message: 'Fecha válida',
                originalValue: dateString,
                parsedDate: momentDate.toDate(),
                formattedDate: momentDate.format('DD/MM/YYYY')
            };
        },

        async checkProductsExistence() {
            this.checkingProducts = true;
            
            try {
                const productIds = [...new Set(this.previewData.map(item => item.productId).filter(id => id))];
                
                if (productIds.length === 0) {
                    this.showAlert("warning", "Advertencia", 'No se encontraron IDs de productos válidos');
                    return;
                }

                console.log('Verificando existencia de productos:', productIds);

                const response = await this.$API.products.checkProductsExistence({ productIds });
                const nonExistentIds = response.data || [];

                console.log('Productos que no existen:', nonExistentIds);

                this.previewData.forEach(item => {
                    if (item.productId) {
                        item.status = nonExistentIds.includes(item.productId) ? 'No existe' : 'Existe';
                    } else {
                        item.status = 'ID inválido';
                    }
                });

                const existingCount = this.previewData.filter(item => item.status === 'Existe').length;
                const nonExistingCount = this.previewData.filter(item => item.status === 'No existe').length;
                
                if (nonExistingCount > 0) {
                    this.showAlert("warning", "Advertencia", 
                        `${existingCount} productos existen, ${nonExistingCount} productos no existen en el sistema`
                    );
                } else {
                    this.showAlert("success", "Éxito", 
                        `Todos los ${existingCount} productos existen en el sistema`
                    );
                }

            } catch (error) {
                console.error('Error al verificar existencia de productos:', error);
                
                this.previewData.forEach(item => {
                    item.status = 'Error al verificar';
                });
                this.showAlert("error", "Error", 'Error al verificar existencia de productos en el backend');
            } finally {
                this.checkingProducts = false;
            }
        },

        toggleFailedProductsFilter() {
            this.showOnlyFailedProducts = !this.showOnlyFailedProducts;
            
            if (this.showOnlyFailedProducts) {
                this.showAlert("info", "Información", 
                    `Mostrando solo ${this.failedProducts.length} productos con errores`
                );
            } else {
                this.showAlert("info", "Información", 
                    `Mostrando todos los ${this.previewData.length} productos`
                );
            }
        },

        async exportFailedProducts() {
            if (this.failedProducts.length === 0) {
                this.showAlert("warning", "Advertencia", 'No hay productos fallidos para exportar');
                return;
            }
            try {
                const exportData = [
                    ['ID Producto', 'Nombre del Producto', 'Lote', 'Costo', 'Fecha Vencimiento', 'Fecha Original', 'Estado del Error', 'Problema con Fecha'],
                    ...this.failedProducts.map(product => [
                        product.productId || '',
                        product.productName || '',
                        product.lote || '',
                        product.costo || '',
                        product.expiryDate || '',
                        product.originalDateValue || '',
                        product.status || '',
                        product.expiryDate && !product.dateValid ? product.dateValidationMessage : ''
                    ])
                ];

                const ws = XLSX.utils.aoa_to_sheet(exportData);

                ws['!cols'] = [
                    { wch: 15 },
                    { wch: 30 },
                    { wch: 12 },
                    { wch: 12 },
                    { wch: 20 },
                    { wch: 20 },
                    { wch: 20 },
                    { wch: 25 }
                ];

                const wb = XLSX.utils.book_new();
                XLSX.utils.book_append_sheet(wb, ws, 'Productos_Fallidos');
                
                const wopts = {
                    bookType: 'xlsx',
                    bookSST: false,
                    type: 'binary',
                    cellStyles: false,
                    compression: true
                };

                const filename = `Productos_Fallidos_${new Date().toISOString().slice(0, 10)}.xlsx`;
                XLSX.writeFile(wb, filename, wopts);

                this.showAlert("success", "Éxito", 
                    `Se exportaron ${this.failedProducts.length} productos fallidos a ${filename}`
                );

            } catch (error) {
                console.error('Error al exportar productos fallidos:', error);
                this.showAlert("error", "Error", "Error al exportar productos fallidos");
            }
        },
        showAlert(type, header, body, options = null) {
            type = type == null ? "danger" : type;
            this.alert.type = type;
            this.alert.header = header;
            this.alert.body = body;
            this.alert.show = true;
            this.alert.options = options != null ? options : null;
        },
        closeAlert() {
            this.alert.show = false;
        },
        acceptAlert() {
            this.alert.show = false;
        },
    }
}
</script>

<style scoped>
.card-step {
    min-height: 400px; 
    width: 100%; 
    padding: 1rem;
    display: flex;
    flex-direction: column;
    justify-content: space-between;
}

.gap-2 {
    gap: 8px;
}

.btnsave {
    background-color: #3498db !important;
    color: white;
}

.btnclean {
    background-color: #F29D35 !important;
    color: white;
}

.btndisable {
    background-color: #41464c !important;
    color: white;
}
</style>

<style>
.mdi-file-excel::before {
    font-size: 32px !important;
}

.data-table .v-data-table__wrapper thead th {
    height: 35px !important;
    text-wrap: nowrap;
    min-width: 100px;
}

.data-table .v-data-table__wrapper tbody tr {
    height: 35px !important;
    text-wrap: nowrap;
    min-width: 100px;
}

.data-table .v-data-table__wrapper tbody td {
    height: 35px !important;
    text-wrap: nowrap;
    min-width: 100px;
}

.tabs-seg .v-slide-group__content {
    justify-content: space-between;
}
</style>
